import React, { useRef } from "react";
import { Button, Table, Tag, Progress, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  MinusOutlined,
  ExpandAltOutlined,
  CloseOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useBulkAction, BulkActionResult } from "../contexts/BulkActionContext";

const BulkActionPanel: React.FC = () => {
  const { isVisible, isMinimized, isRunning, results, actionLabel, setMinimized, dismiss } =
    useBulkAction();
  const panelRef = useRef<HTMLDivElement>(null);

  if (!isVisible) return null;

  const done = results.filter((r) => r.status !== "pending").length;
  const total = results.length;
  const successCount = results.filter((r) => r.status === "success").length;
  const errorCount = results.filter((r) => r.status === "error").length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  const downloadCsv = () => {
    const header = "Terminal ID,Status,Response\n";
    const rows = results
      .map((r) => `"${r.terminalId}","${r.status}","${r.response.replace(/"/g, '""')}"`)
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bulk-menu-action-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const statusColor = isRunning ? "#441752" : errorCount > 0 ? "#ef4444" : "#22c55e";

  /* ── Minimized pill ── */
  if (isMinimized) {
    // Determine pill color based on completion status
    let pillBg = "bg-neo-primary border-neo-primary/80"; // running
    if (!isRunning) {
      if (errorCount === 0) {
        pillBg = "bg-green-600 border-green-600/80";       // full success
      } else if (successCount === 0) {
        pillBg = "bg-red-600 border-red-600/80";           // full failed
      } else {
        pillBg = "bg-amber-500 border-amber-500/80";       // partial success
      }
    }

    return (
      <div
        className={`fixed bottom-6 right-6 z-[1100] flex items-center gap-x-3 ${pillBg} border rounded-2xl shadow-2xl px-4 py-3 cursor-pointer hover:shadow-xl transition-all duration-300 select-none`}
        style={{ minWidth: 260 }}
        onClick={() => setMinimized(false)}
        role="button"
        aria-label="Expand bulk action panel"
      >
        {/* Status dot */}
        <div className="relative flex-shrink-0">
          <div className="w-3 h-3 rounded-full bg-white" />
          {isRunning && (
            <div className="absolute inset-0 w-3 h-3 rounded-full animate-ping bg-white opacity-60" />
          )}
        </div>

        {/* Label + progress */}
        <div className="flex flex-col flex-1 min-w-0">
          <Typography className="font-poppins-500 text-sm text-white truncate">
            {actionLabel}
          </Typography>
          <Typography className="font-poppins-400 text-xs text-white/70">
            {isRunning
              ? `${done} / ${total} terminals`
              : `Done · ${successCount} ok${errorCount > 0 ? ` · ${errorCount} failed` : ""}`}
          </Typography>
        </div>

        {/* Expand icon */}
        <ExpandAltOutlined className="text-white/80 text-sm flex-shrink-0" />
      </div>
    );
  }

  /* ── Expanded panel ── */
  return (
    <div
      ref={panelRef}
      className="fixed bottom-6 right-6 z-[1100] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      style={{ width: 560, maxHeight: "80vh" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-neo-primary to-neo-primary/85 rounded-t-2xl">
        <div className="flex items-center gap-x-3">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <ThunderboltOutlined className="text-white text-base" />
          </div>
          <div>
            <Typography className="font-poppins-600 text-white text-sm leading-tight">
              {actionLabel}
            </Typography>
            <Typography className="font-poppins-400 text-white/70 text-xs">
              {isRunning ? "Processing terminals..." : "Completed"}
            </Typography>
          </div>
        </div>

        <div className="flex items-center gap-x-1">
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined className="text-white/80" />}
            onClick={() => setMinimized(true)}
            className="hover:bg-white/20 border-0 rounded-lg"
            title="Minimize"
          />
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined className="text-white/80" />}
            onClick={dismiss}
            disabled={isRunning}
            className="hover:bg-white/20 border-0 rounded-lg disabled:opacity-40"
            title={isRunning ? "Cannot close while running" : "Close"}
          />
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-x-4">
            <span className="flex items-center gap-x-1.5 text-xs font-poppins-400 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />
              {total} total
            </span>
            <span className="flex items-center gap-x-1.5 text-xs font-poppins-500 text-green-600">
              <CheckCircleOutlined />
              {successCount} success
            </span>
            {errorCount > 0 && (
              <span className="flex items-center gap-x-1.5 text-xs font-poppins-500 text-red-500">
                <CloseCircleOutlined />
                {errorCount} failed
              </span>
            )}
          </div>
          <span className="text-xs font-poppins-600 text-neo-primary">{percent}%</span>
        </div>
        <Progress
          percent={percent}
          showInfo={false}
          strokeColor={isRunning ? "#441752" : errorCount > 0 ? "#ef4444" : "#22c55e"}
          trailColor="#f3f4f6"
          strokeWidth={6}
          className="mb-0"
        />
      </div>

      {/* Results table */}
      <div className="flex-1 overflow-auto px-5 pb-2">
        <Table<BulkActionResult>
          size="small"
          pagination={false}
          dataSource={results}
          rowKey="terminalId"
          className="bulk-action-table"
          columns={[
            {
              title: <span className="font-poppins-500 text-xs text-gray-600">Terminal ID</span>,
              dataIndex: "terminalId",
              key: "terminalId",
              width: 130,
              render: (v: string) => (
                <span className="font-poppins-600 text-sm text-gray-800">{v}</span>
              ),
            },
            {
              title: <span className="font-poppins-500 text-xs text-gray-600">Status</span>,
              dataIndex: "status",
              key: "status",
              width: 100,
              render: (v: BulkActionResult["status"]) =>
                v === "pending" ? (
                  <Tag
                    icon={<LoadingOutlined spin />}
                    className="font-poppins-400 text-xs border-0 bg-gray-100 text-gray-500 rounded-full px-2"
                  >
                    Pending
                  </Tag>
                ) : v === "success" ? (
                  <Tag
                    icon={<CheckCircleOutlined />}
                    className="font-poppins-400 text-xs border-0 bg-green-50 text-green-700 rounded-full px-2"
                  >
                    Success
                  </Tag>
                ) : (
                  <Tag
                    icon={<CloseCircleOutlined />}
                    className="font-poppins-400 text-xs border-0 bg-red-50 text-red-600 rounded-full px-2"
                  >
                    Failed
                  </Tag>
                ),
            },
            {
              title: <span className="font-poppins-500 text-xs text-gray-600">Response</span>,
              dataIndex: "response",
              key: "response",
              render: (v: string, record) => (
                <span
                  className={`font-poppins-400 text-xs ${
                    record.status === "error" ? "text-red-500" : "text-gray-600"
                  }`}
                >
                  {v}
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 rounded-b-2xl">
        <Typography className="font-poppins-400 text-xs text-gray-400">
          {isRunning ? "Requests running ..." : "All requests completed"}
        </Typography>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={downloadCsv}
          disabled={isRunning}
          className="rounded-full px-4 font-poppins-400 text-xs border border-neo-primary text-neo-primary bg-white hover:bg-neo-primary/5 disabled:opacity-40"
        >
          Export CSV
        </Button>
      </div>
    </div>
  );
};

export default BulkActionPanel;
